import {
  Button,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  UpdateIconButton,
  formatBoolean,
  formatDate,
  formatSchoolYear,
  pn,
} from "@compass/core";
import { ListBeProperty } from "components";
import {
  Permission,
  PermissionAction,
  StaffEmploymentListDto,
  StaffEmploymentListFilter,
  StaffEmploymentListResource,
} from "generated/openApi";
import { useList, useRouter } from "hooks";
import "moment-timezone";
import { staffEmploymentPageRoute } from "pages/staff/staff-employments/StaffEmploymentPage/StaffEmploymentPage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
// import { matchPath } from "react-router";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const Employments = (props: { initialLoad: boolean; staffId: number }) => {
  const ROOT_PERMISSION: Permission = Permission.StaffEmployment;
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<StaffEmploymentListFilter>({
    employmentFilter: { staffId: props.staffId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<StaffEmploymentListDto>("startDate"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<StaffEmploymentListDto[]>([]);
  const [resource, setResource] = useState<StaffEmploymentListResource>({});
  const properties = resource.properties || [];

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.staffEmployments.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.staffEmployments.getListResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  return (
    <>
      <ListContainer
        fullHeight
        header={
          <Stack direction="row" justifyContent={"space-between"} alignItems={"center"}>
            <Typography variant="inherit">Employments</Typography>
            {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
              <Button
                onClick={() =>
                  navigate({
                    pathname: staffEmploymentPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "dialog" }),
                    search: staffEmploymentPageRoute.getOptionalParams?.({
                      staffId: props.staffId,
                    }),
                  })
                }
                variant="outlined"
              >
                + New
              </Button>
            )}
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StaffEmploymentListDto>("schoolYear"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StaffEmploymentListDto>("school"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StaffEmploymentListDto>("startDate"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StaffEmploymentListDto>("endDate"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<StaffEmploymentListDto>("educator"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems?.map((listItem) => (
                  <TableRow
                    key={listItem.id}
                    onClick={() =>
                      navigate({
                        pathname: staffEmploymentPageRoute.getPath({
                          id: listItem.id,
                          crudMode: "view",
                          screenType: "dialog",
                        }),
                      })
                    }
                  >
                    <TableCell>{formatSchoolYear(listItem.schoolYear)}</TableCell>
                    <TableCell>{listItem.school}</TableCell>
                    <TableCell>{formatDate(listItem.startDate)}</TableCell>
                    <TableCell>{formatDate(listItem.endDate)}</TableCell>
                    <TableCell align="right">{formatBoolean(listItem.educator)}</TableCell>
                    <TableCell type="action">
                      {isAuthorized(ROOT_PERMISSION, PermissionAction.Update) && (
                        <UpdateIconButton color="primary"
                          onClick={() =>
                            navigate({
                              pathname: staffEmploymentPageRoute.getPath({
                                id: listItem.id,
                                crudMode: "edit",
                                screenType: "dialog",
                              }),
                            })
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider
        value={{
          loadList: (options) => {
            loadList(listFilter, { resetPage: options?.resetPage });
          },
        }}
      >
        {/* Outlet will only render for the designated route to prevent conflicts with others on parent Page */}
        {matchTemplatePath(staffEmploymentPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
